/**
 * UK Bank Holiday Utilities (England & Wales)
 * ============================================
 * Calculates UK bank holidays for a given year and checks whether
 * a specific date is a bank holiday.
 */

// Anonymous Gregorian algorithm to calculate Easter Sunday
const getEasterSunday = (year) => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
};

// Returns all UK bank holiday dates (England/Wales) for a given year
export const getUKBankHolidays = (year) => {
  const date = (m, d) => new Date(year, m, d);
  const holidays = [];

  // New Year's Day (substitute if it falls on a weekend)
  const jan1Day = date(0, 1).getDay();
  if (jan1Day === 6) holidays.push(date(0, 3));       // Sat → Mon Jan 3
  else if (jan1Day === 0) holidays.push(date(0, 2));  // Sun → Mon Jan 2
  else holidays.push(date(0, 1));

  // Good Friday (Easter − 2 days) and Easter Monday (Easter + 1 day)
  const easter = getEasterSunday(year);
  holidays.push(new Date(easter.getFullYear(), easter.getMonth(), easter.getDate() - 2));
  holidays.push(new Date(easter.getFullYear(), easter.getMonth(), easter.getDate() + 1));

  // Early May Bank Holiday (first Monday of May)
  const may1Day = date(4, 1).getDay();
  holidays.push(date(4, 1 + (8 - may1Day) % 7));

  // Spring Bank Holiday (last Monday of May)
  const may31Day = date(4, 31).getDay();
  holidays.push(date(4, 31 - (may31Day + 6) % 7));

  // Summer Bank Holiday (last Monday of August)
  const aug31Day = date(7, 31).getDay();
  holidays.push(date(7, 31 - (aug31Day + 6) % 7));

  // Christmas Day and Boxing Day (with substitution rules)
  const dec25Day = date(11, 25).getDay();
  if (dec25Day === 6) {
    holidays.push(date(11, 27), date(11, 28)); // Sat → Mon Dec 27; Boxing → Tue Dec 28
  } else if (dec25Day === 0) {
    holidays.push(date(11, 26), date(11, 27)); // Sun → Mon Dec 26; Boxing → Tue Dec 27
  } else if (dec25Day === 5) {
    holidays.push(date(11, 25), date(11, 28)); // Fri; Boxing Sat → Mon Dec 28
  } else {
    holidays.push(date(11, 25), date(11, 26));
  }

  return holidays;
};

// Returns true if the given date is a UK bank holiday
export const isUKBankHoliday = (date) => {
  const year = date.getFullYear();
  const holidays = getUKBankHolidays(year);
  return holidays.some(h =>
    h.getFullYear() === year &&
    h.getMonth() === date.getMonth() &&
    h.getDate() === date.getDate()
  );
};
